import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCw, Upload } from "lucide-react";

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export default function Settings() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { (async () => { const { data } = await api.get("/settings"); setS(data); })(); }, []);

  if (!s) return null;

  const upd = (k, v) => setS({ ...s, [k]: v });
  const updRate = (cur, v) => setS({ ...s, exchange_rates: { ...(s.exchange_rates || {}), [cur]: Number(v) }, manual_override: true });

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // aynı dosyayı tekrar seçebilmek için
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
      toast.error("Sadece PNG veya JPG dosyaları desteklenir.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(`Dosya çok büyük (${(file.size / 1024 / 1024).toFixed(2)} MB). Maksimum 2 MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      upd("company_logo_url", String(reader.result || ""));
      toast.success(`Logo yüklendi: ${file.name} (${(file.size / 1024).toFixed(0)} KB)`);
    };
    reader.onerror = () => toast.error("Dosya okunamadı.");
    reader.readAsDataURL(file);
  };

  const save = async () => {
    const { data } = await api.put("/settings", s);
    setS(data);
    toast.success("Ayarlar kaydedildi");
  };

  const fetchTcmb = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/settings/fetch-tcmb");
      setS(data);
      toast.success(`TCMB kurları güncellendi — ${new Date(data.exchange_rate_updated_at).toLocaleString("tr-TR")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "TCMB'ye erişilemedi. Son kayıtlı kur kullanılıyor.");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Sistem</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight mt-1">Ayarlar</h2>
        </div>
        <Button data-testid="save-settings" onClick={save} className="bg-primary hover:bg-orange-600 text-white">Ayarları Kaydet</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 border-border">
          <h3 className="font-heading font-semibold mb-4">Vergi ve Maliyet Yüzdeleri</h3>
          <div className="grid grid-cols-2 gap-3">
            <F label="SGK Hesaplama Yöntemi">
              <Select value={s.sgk_method || "percent"} onValueChange={(v) => upd("sgk_method", v)}>
                <SelectTrigger data-testid="sgk-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Ücrete göre yüzde</SelectItem>
                  <SelectItem value="daily_fixed">Kişi başı günlük sabit tutar</SelectItem>
                </SelectContent>
              </Select>
            </F>
            {(s.sgk_method || "percent") === "percent" ? (
              <F label="SGK %">
                <Input data-testid="sgk-percent" type="number" step="0.1" value={s.sgk_percent} onChange={(e) => upd("sgk_percent", Number(e.target.value))} />
              </F>
            ) : (
              <F label="Günlük SGK Tutarı (TL / kişi × gün)">
                <Input data-testid="sgk-daily-amount" type="number" step="1" value={s.sgk_daily_amount ?? 0} onChange={(e) => upd("sgk_daily_amount", Number(e.target.value))} placeholder="Örn: 370" />
              </F>
            )}
            <F label="KDV %"><Input type="number" step="0.1" value={s.vat_percent} onChange={(e) => upd("vat_percent", Number(e.target.value))} /></F>
            <F label="Genel Gider %"><Input type="number" step="0.1" value={s.overhead_percent} onChange={(e) => upd("overhead_percent", Number(e.target.value))} /></F>
            <F label="Beklenmeyen %"><Input type="number" step="0.1" value={s.contingency_percent} onChange={(e) => upd("contingency_percent", Number(e.target.value))} /></F>
            <F label="Risk %"><Input type="number" step="0.1" value={s.risk_percent} onChange={(e) => upd("risk_percent", Number(e.target.value))} /></F>
            <F label="Varsayılan Kâr %"><Input type="number" step="0.1" value={s.profit_percent} onChange={(e) => upd("profit_percent", Number(e.target.value))} /></F>
            <F label="Günlük Mesai (saat)"><Input type="number" step="0.5" value={s.daily_hours} onChange={(e) => upd("daily_hours", Number(e.target.value))} /></F>
            <F label="Mesai Çarpanı"><Input type="number" step="0.1" value={s.overtime_multiplier} onChange={(e) => upd("overtime_multiplier", Number(e.target.value))} /></F>
            <F label="Varsayılan Fire %"><Input type="number" step="0.1" value={s.default_waste_percent} onChange={(e) => upd("default_waste_percent", Number(e.target.value))} /></F>
          </div>
        </Card>

        <Card className="p-5 border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold">Döviz Kurları</h3>
            <Button data-testid="fetch-tcmb-btn" size="sm" variant="outline" onClick={fetchTcmb} disabled={loading}>
              <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> TCMB'den Çek
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="USD"><Input type="number" step="0.0001" value={s.exchange_rates?.USD ?? 0} onChange={(e) => updRate("USD", e.target.value)} data-testid="rate-usd" /></F>
            <F label="EUR"><Input type="number" step="0.0001" value={s.exchange_rates?.EUR ?? 0} onChange={(e) => updRate("EUR", e.target.value)} data-testid="rate-eur" /></F>
            <F label="TRY"><Input type="number" value={s.exchange_rates?.TRY ?? 1} disabled /></F>
          </div>
          <div className="mt-4 text-xs text-muted-foreground space-y-1">
            <div>Kaynak: <b>{s.exchange_rate_source || "manual"}</b> {s.manual_override && "(Manuel Override Aktif)"}</div>
            <div>Son güncelleme: {s.exchange_rate_updated_at ? new Date(s.exchange_rate_updated_at).toLocaleString("tr-TR") : "-"}</div>
          </div>
        </Card>

        <Card className="p-5 border-border">
          <h3 className="font-heading font-semibold mb-4">Tedarikçi Skor Ağırlıkları</h3>
          <div className="grid grid-cols-2 gap-3">
            <F label="Maliyet Ağırlığı (0-1)"><Input type="number" step="0.05" value={s.supplier_score_cost_weight} onChange={(e) => upd("supplier_score_cost_weight", Number(e.target.value))} /></F>
            <F label="Teslim Ağırlığı (0-1)"><Input type="number" step="0.05" value={s.supplier_score_delivery_weight} onChange={(e) => upd("supplier_score_delivery_weight", Number(e.target.value))} /></F>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Toplam 1.0 olmalıdır. Örn: 0.7 maliyet + 0.3 teslim.</p>
        </Card>

        <Card className="p-5 border-border lg:col-span-2">
          <h3 className="font-heading font-semibold mb-4">Firma Marka Kimliği (PDF Üstbilgi/Altbilgi)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Firma Unvanı">
              <Input data-testid="company-name" value={s.company_name || ""} onChange={(e) => upd("company_name", e.target.value)} placeholder="Doyen Metalürji A.Ş." />
            </F>
            <div className="md:col-span-1">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Logo (PNG / JPG, maks. 2 MB)</Label>
              <div className="mt-1 flex items-start gap-3">
                <div
                  data-testid="logo-preview"
                  className="w-16 h-16 rounded-md border border-border bg-slate-50 dark:bg-slate-800 grid place-items-center overflow-hidden shrink-0"
                >
                  {s.company_logo_url ? (
                    <img src={s.company_logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Yok</span>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    data-testid="logo-file-input"
                    onChange={handleLogoFile}
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="logo-file-select"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={14} className="mr-1" /> Logo Dosyası Seç
                    </Button>
                    {s.company_logo_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        data-testid="logo-clear"
                        onClick={() => upd("company_logo_url", "")}
                        className="text-destructive hover:text-destructive"
                      >
                        Kaldır
                      </Button>
                    )}
                  </div>
                  <Input
                    data-testid="company-logo-url"
                    value={s.company_logo_url || ""}
                    onChange={(e) => upd("company_logo_url", e.target.value)}
                    placeholder="https://.../logo.png veya dosyadan seçin"
                    className="text-xs font-mono"
                  />
                </div>
              </div>
            </div>
            <F label="Adres">
              <Input data-testid="company-address" value={s.company_address || ""} onChange={(e) => upd("company_address", e.target.value)} placeholder="Organize Sanayi Bölgesi ..." />
            </F>
            <F label="Telefon">
              <Input data-testid="company-phone" value={s.company_phone || ""} onChange={(e) => upd("company_phone", e.target.value)} placeholder="+90 262 xxx xx xx" />
            </F>
            <F label="E-posta">
              <Input data-testid="company-email" value={s.company_email || ""} onChange={(e) => upd("company_email", e.target.value)} placeholder="info@firma.com.tr" />
            </F>
            <F label="Web Sitesi">
              <Input data-testid="company-website" value={s.company_website || ""} onChange={(e) => upd("company_website", e.target.value)} placeholder="www.firma.com.tr" />
            </F>
            <F label="Yetkili Adı Soyadı">
              <Input data-testid="authorized-person" value={s.authorized_person || ""} onChange={(e) => upd("authorized_person", e.target.value)} placeholder="Ad Soyad" />
            </F>
            <F label="Yetkili Ünvanı">
              <Input data-testid="authorized-title" value={s.authorized_title || ""} onChange={(e) => upd("authorized_title", e.target.value)} placeholder="Genel Müdür" />
            </F>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Bu bilgiler tüm müşteri teklifi PDF çıktılarının üstüne (logo + firma iletişim) ve altına (yetkili + imza + kaşe alanı) basılır. İç maliyet bilgileri PDF'e asla yansımaz.
          </p>
        </Card>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
